import { useState, useEffect } from 'react';

interface DownloadStat {
  platform: string;
  label: string;
  emoji: string;
  count: number | null;
}

const WORKER_BASE = 'https://nameless-math-e510.cruzfind.workers.dev';

export function SignInPanel() {
  const [stats, setStats] = useState<DownloadStat[]>([
    { platform: 'Android_APK', label: 'Android APK', emoji: '📥', count: null },
    { platform: 'PWA_Install', label: 'PWA Container', emoji: '💻', count: null },
    { platform: 'Desktop_Web_App', label: 'Desktop Web', emoji: '🖥️', count: null },
  ]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [voucherBatchSize, setVoucherBatchSize] = useState(10);
  const [voucherBatchTier, setVoucherBatchTier] = useState<'1499'>('1499');
  const [lastMintLog, setLastMintLog] = useState<string | null>(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchStats = async () => {
    setIsRefreshing(true);
    setFetchError(null);
    try {
      const results = await Promise.allSettled(
        stats.map((s) =>
          fetch(`${WORKER_BASE}/stats?platform=${s.platform}`, { mode: 'cors' })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        )
      );
      setStats((prev) =>
        prev.map((s, i) => {
          const r = results[i];
          if (r && r.status === 'fulfilled' && typeof r.value?.count === 'number') {
            return { ...s, count: r.value.count };
          }
          return s;
        })
      );
    } catch {
      setFetchError('Worker endpoint unreachable. Stats will update when connected.');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMintBatch = () => {
    const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setLastMintLog(
      `Queued ${voucherBatchSize} × ₱${voucherBatchTier} voucher block(s) for TIN 466-338-057-0000 at ${stamp}. Open the Keygen Vault to finalize cryptographic mint.`
    );
  };

  return (
    <div className="min-h-screen bg-[#030306] text-slate-100 px-4 py-10 relative overflow-hidden transition-all duration-300">
      
      {/* ──────────────────────── AMBIENT GLOW SYSTEM ──────────────────────── */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[50%] bg-[radial-gradient(ellipse_at_top_left,rgba(99,102,241,0.08),transparent_60%)] pointer-events-none animate-pulse-glow" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[radial-gradient(circle_at_bottom,rgba(168,85,247,0.06),transparent_50%)] pointer-events-none animate-pulse-glow" />
      
      <div className="max-w-5xl mx-auto space-y-8 relative z-10">
        
        {/* Header Console Banner */}
        <div className="glass-panel-heavy border border-white/[0.06] rounded-[28px] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-full blur-2xl pointer-events-none" />
          
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="text-[10px] font-extrabold tracking-widest bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent uppercase">
                Private Admin Controller · /admin-dashboard
              </span>
              {isOnline ? (
                <span className="px-2 py-0.5 text-[8px] font-extrabold bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 rounded-full uppercase tracking-wider">Sync Live</span>
              ) : (
                <span className="px-2 py-0.5 text-[8px] font-extrabold bg-indigo-500/10 border border-indigo-500/25 text-indigo-400 rounded-full uppercase tracking-wider">Local Only</span>
              )}
            </div>
            <h1 className="font-display text-2xl font-black text-white tracking-tight">
              CruzFind Platform Control Deck
            </h1>
            <p className="text-[10px] text-slate-450 uppercase font-mono-custom tracking-wider">
              CruzFind Software Development Services · Ramon, Isabela ·{' '}
              <span className="font-semibold text-slate-200">TIN 466-338-057-0000</span>
            </p>
          </div>
          
          <button
            type="button"
            onClick={fetchStats}
            disabled={isRefreshing}
            className="text-xs font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:opacity-95 text-white px-5 py-3 rounded-xl transition-all disabled:opacity-50 active:scale-[0.98] shadow-lg shadow-indigo-600/15 shrink-0 self-stretch md:self-auto hover:scale-[1.01]"
          >
            {isRefreshing ? '↻ Refreshing...' : '↻ Refresh Stats'}
          </button>
        </div>

        {/* Row 1 — Platform Downloads Monitor */}
        <section className="glass-panel border border-white/[0.04] rounded-[28px] p-6 space-y-5 shadow-xl transition-all hover:border-white/[0.08] duration-300">
          <div className="flex items-center justify-between">
            <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-450">
              📊 Total Platform Downloads Monitor
            </h2>
            <span className="text-[8px] font-bold uppercase tracking-wider bg-black/60 border border-white/[0.06] text-indigo-400 px-3 py-1 rounded-full backdrop-blur-md">
              Cloudflare Worker Metrics
            </span>
          </div>
          
          {fetchError && (
            <p className="text-[11px] text-amber-300 bg-amber-950/20 border border-amber-900/30 rounded-xl px-4 py-3 leading-relaxed">
              {fetchError}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.map(({ platform, label, emoji, count }) => (
              <div
                key={platform}
                className="flex flex-col items-center justify-center p-6 bg-black/40 border border-white/[0.04] rounded-2xl text-center hover:border-indigo-500/20 hover:scale-[1.02] transition-all duration-300 shadow-inner group"
              >
                <span className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">{emoji}</span>
                <div className="font-display text-4xl font-black text-white tracking-tight tabular-nums">
                  {count === null ? (
                    <span className="text-slate-700 text-lg animate-pulse">—</span>
                  ) : (
                    count.toLocaleString()
                  )}
                </div>
                <div className="text-[9px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Row 2 — Voucher Block Generator */}
        <section className="glass-panel border border-white/[0.04] rounded-[28px] p-6 space-y-5 shadow-xl transition-all hover:border-white/[0.08] duration-300">
          <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-450">
            🔑 Voucher Block Generator
          </h2>
          <p className="text-xs text-slate-400 leading-relaxed">
            Mint cryptographic activation vouchers bound to TIN{' '}
            <span className="font-mono-custom font-semibold text-indigo-300">466-338-057-0000</span> at the locked{' '}
            <span className="font-semibold text-white">₱1,499.00</span> Selyo Master Key tier.
            All blocks are deterministically signed under IPO Application No.{' '}
            <span className="font-mono-custom font-semibold text-indigo-300">22026050461A</span>.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block space-y-2">
              <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500">
                Batch Size
              </span>
              <input
                type="number"
                min={1}
                max={500}
                value={voucherBatchSize}
                onChange={(e) => setVoucherBatchSize(Math.max(1, Number(e.target.value)))}
                className="w-full px-4 py-3 bg-black/60 border border-white/[0.06] rounded-xl text-xs text-white focus:outline-none focus:border-indigo-500/80 focus:ring-4 focus:ring-indigo-500/10 transition-all duration-300"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500">
                Tier
              </span>
              <select
                value={voucherBatchTier}
                onChange={(e) => setVoucherBatchTier(e.target.value as '1499')}
                className="w-full px-4 py-3 bg-black/60 border border-white/[0.06] rounded-xl text-xs text-white focus:outline-none focus:border-indigo-500/80 focus:ring-4 focus:ring-indigo-500/10 transition-all duration-300"
              >
                <option value="1499">₱1,499.00 — Selyo Master Key</option>
              </select>
            </label>

            <div className="flex items-end">
              <button
                type="button"
                onClick={handleMintBatch}
                className="w-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:opacity-95 text-white py-3.5 rounded-xl transition-all shadow-md active:scale-[0.99] hover:scale-[1.01] duration-300"
              >
                Queue Voucher Block
              </button>
            </div>
          </div>

          {lastMintLog && (
            <p className="text-[11px] font-mono-custom text-emerald-400 bg-emerald-950/20 border border-emerald-900/30 rounded-xl px-4 py-3 leading-relaxed">
              {lastMintLog}
            </p>
          )}

          <div className="pt-2">
            <a
              href="#admin"
              className="inline-flex items-center gap-2 text-xs font-bold bg-[#121216]/60 backdrop-blur-md hover:bg-[#1b1b22] text-slate-200 hover:text-white px-5 py-3 rounded-xl transition-all shadow-md active:scale-[0.99] border border-white/[0.06] hover:scale-[1.01] duration-300"
            >
              🔐 Open Keygen Vault →
            </a>
          </div>
        </section>

        {/* Row 3 — Active Application Modules Deck */}
        <section className="glass-panel border border-white/[0.04] rounded-[28px] p-6 space-y-5 shadow-xl transition-all hover:border-white/[0.08] duration-300">
          <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-450">
            🧩 Active Application Modules Deck
          </h2>
          <div className="space-y-3">
            {/* Selyo: Online */}
            <div className="flex items-center justify-between p-5 bg-black/60 border border-white/[0.04] rounded-2xl transition-all hover:border-indigo-500/10">
              <div>
                <div className="text-sm font-bold text-slate-200">Selyo</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  EOPT Act · Local-First Tax &amp; Invoicing Engine · powered by CruzFind
                </div>
              </div>
              <span className="text-[8px] font-black bg-indigo-500/10 border border-indigo-500/25 text-indigo-400 px-3 py-1 rounded-full uppercase tracking-wider shrink-0">
                Online
              </span>
            </div>

            {/* + Add Future Core App Module */}
            <button
              type="button"
              onClick={() =>
                alert(
                  'Future Core App Module provisioning coming soon. Contact the development team to register a new module under the CruzFind umbrella.'
                )
              }
              className="w-full flex items-center justify-center gap-2 p-5 border-2 border-dashed border-white/[0.04] rounded-2xl text-xs text-slate-500 hover:border-indigo-500/20 hover:text-slate-400 transition-all duration-300"
            >
              <span className="text-base">＋</span>
              Add Future Core App Module
            </button>
          </div>
        </section>

        {/* Footer */}
        <p className="text-[10px] text-slate-550 text-center pb-4 tracking-wide font-display">
          IPO Application No: 22026050461A · CruzFind Software Development Services · Ramon, Isabela, Philippines · All systems nominal
        </p>
      </div>
    </div>
  );
}
